import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { ContainerFacilityService } from '../container-facility.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerTrackingService } from '../container-tracking.service';
import { ContainerCase } from '../models/container-case.model';
import { ContainerEvent } from '../models/container-event.model';
import { ContainerFacility } from '../models/container-facility.model';
import { ContainerLegHandoff, ContainerLegHandoffContext } from '../models/container-leg-handoff.model';
import { ContainerStorageLeg } from '../models/container-storage-leg.model';
import { ContainerTransportLeg } from '../models/container-transport-leg.model';
import {
  ContainerYardAppointment,
  ContainerYardArea,
  ContainerYardChargeAccrual,
  ContainerYardChargeRule,
  ContainerYardEir,
  ContainerYardMove,
  ContainerYardSlot,
  ContainerYardVisit
} from '../models/container-terminal-yard.model';

@Component({
  selector: 'app-container-storage-leg-operations',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DatePickerModule,
    DialogModule,
    IftaLabelModule,
    InputTextModule,
    ProgressBarModule,
    ReactiveFormsModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageDataPanelComponent,
    PageDetailHeroComponent,
    PageShellComponent
  ],
  providers: [MessageService],
  templateUrl: './container-storage-leg-operations.component.html',
  styleUrls: [
    '../container-case-detail/container-case-detail.component.css',
    './container-storage-leg-operations.component.css'
  ]
})
export class ContainerStorageLegOperationsComponent implements OnInit {
  isLoading = false;
  isSubmitting = false;
  canManageStorage = false;

  caseItem?: ContainerCase;
  storageLeg?: ContainerStorageLeg;
  storageLegs: ContainerStorageLeg[] = [];
  transportLegs: ContainerTransportLeg[] = [];
  handoffs: ContainerLegHandoff[] = [];
  timeline: ContainerEvent[] = [];
  facilities: ContainerFacility[] = [];
  supplierClients: CrmClient[] = [];
  yardAreas: ContainerYardArea[] = [];
  yardSlots: ContainerYardSlot[] = [];
  yardVisits: ContainerYardVisit[] = [];
  yardVisit?: ContainerYardVisit;
  yardMoves: ContainerYardMove[] = [];
  charges: ContainerYardChargeAccrual[] = [];
  chargeRules: ContainerYardChargeRule[] = [];
  eirs: ContainerYardEir[] = [];
  appointments: ContainerYardAppointment[] = [];

  receiveDialogVisible = false;
  exitDialogVisible = false;
  slotDialogVisible = false;
  holdDialogVisible = false;
  releaseDialogVisible = false;
  yardMoveDialogVisible = false;
  gateOutDialogVisible = false;

  handoffForm!: FormGroup;
  slotForm!: FormGroup;
  holdForm!: FormGroup;
  releaseForm!: FormGroup;
  yardMoveForm!: FormGroup;
  gateOutForm!: FormGroup;

  yardMoveTypeOptions = [
    { label: 'Yard Move', value: 'YARD_MOVE' },
    { label: 'Slot Assign', value: 'SLOT_ASSIGN' },
    { label: 'Inspection In', value: 'INSPECTION_IN' },
    { label: 'Inspection Out', value: 'INSPECTION_OUT' },
    { label: 'Hold', value: 'HOLD' },
    { label: 'Release Hold', value: 'RELEASE_HOLD' },
    { label: 'Dock Move', value: 'DOCK_MOVE' }
  ];

  conditionOptions = [
    { label: 'Good', value: 'GOOD' },
    { label: 'Damaged', value: 'DAMAGED' },
    { label: 'Unknown', value: 'UNKNOWN' }
  ];

  yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private permissionService: PermissionService,
    private sessionStorageService: SessionStorageService,
    private crmService: CrmService,
    private trackingService: ContainerTrackingService,
    private facilityService: ContainerFacilityService,
    private terminalYardService: ContainerTerminalYardService
  ) {}

  ngOnInit(): void {
    this.canManageStorage = this.permissionService.hasPermission(app_permissions.container_tracking.storage.manage);
    this.initForms();
    this.route.paramMap.subscribe(params => {
      const caseId = Number(params.get('caseId'));
      const storageLegId = Number(params.get('storageLegId'));
      if (caseId && storageLegId) {
        this.load(caseId, storageLegId);
      }
    });
  }

  load(caseId: number, storageLegId: number): void {
    this.isLoading = true;
    forkJoin({
      caseItem: this.trackingService.findCaseById(caseId),
      storageLegs: this.trackingService.findStorageLegs(caseId),
      transportLegs: this.trackingService.findTransportLegs(caseId),
      handoffs: this.trackingService.findHandoffs(caseId),
      timeline: this.trackingService.findTimeline(caseId)
    }).subscribe({
      next: result => {
        this.caseItem = result.caseItem;
        this.storageLegs = result.storageLegs || [];
        this.transportLegs = result.transportLegs || [];
        this.handoffs = result.handoffs || [];
        this.timeline = result.timeline || [];
        this.storageLeg = this.storageLegs.find(leg => leg.id === storageLegId);
        this.isLoading = false;
        this.loadYardContext();
      },
      error: error => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Load failed', detail: error?.error?.message || 'Could not load storage control.' });
      }
    });
  }

  goBack(): void {
    if (this.caseItem?.id) {
      this.router.navigate(['/container-tracking/cases/view', this.caseItem.id], { queryParams: { tab: 'storage-legs' } });
    }
  }

  openReceiveDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.resetHandoffForm('receive');
    this.receiveDialogVisible = true;
  }

  openExitDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.resetHandoffForm('exit');
    this.exitDialogVisible = true;
  }

  openSlotDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.slotForm.reset({
      yardAreaId: this.storageLeg?.yardAreaId || null,
      yardSlotId: this.storageLeg?.yardSlotId || null,
      slotCode: this.storageLeg?.slotCode || '',
      notes: this.storageLeg?.notes || ''
    });
    this.slotDialogVisible = true;
  }

  openHoldDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.holdForm.reset({ eventTime: new Date(), notes: this.storageLeg?.notes || '' });
    this.holdDialogVisible = true;
  }

  openReleaseDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.releaseForm.reset({ eventTime: new Date(), notes: '' });
    this.releaseDialogVisible = true;
  }

  openYardMoveDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.yardMoveForm.reset({
      moveType: 'YARD_MOVE',
      toAreaId: this.yardVisit?.currentAreaId || this.storageLeg?.yardAreaId || null,
      toSlotId: this.yardVisit?.currentSlotId || this.storageLeg?.yardSlotId || null,
      eventTime: new Date(),
      referenceNumber: '',
      remarks: ''
    });
    this.yardMoveDialogVisible = true;
  }

  openGateOutDialog(): void {
    if (this.isReferenceOnlyStorageLeg()) return;
    this.gateOutForm.reset({
      gateOutAt: new Date(),
      emptyReturn: false,
      outboundTransportLegId: this.storageLeg?.outboundTransportLegId || null,
      referenceNumber: '',
      sealVerified: this.yardVisit?.sealVerified ?? true,
      sealMismatchReason: this.yardVisit?.sealMismatchReason || '',
      conditionStatus: this.yardVisit?.conditionStatus || 'UNKNOWN',
      damageObserved: !!this.yardVisit?.damageObserved,
      damageNotes: this.yardVisit?.damageNotes || '',
      remarks: ''
    });
    this.gateOutDialogVisible = true;
  }

  saveReceive(): void {
    if (!this.caseItem?.id || !this.storageLeg?.id) {
      return;
    }
    const value = this.handoffForm.getRawValue();
    const overrideReason = (value.overrideReason || '').trim();
    if (!value.sourceTransportLegId && !overrideReason) {
      this.messageService.add({ severity: 'warn', summary: 'Inbound transport required', detail: 'Select inbound transport or record an override reason.' });
      return;
    }
    this.submit(this.trackingService.receiveStorageLeg(this.caseItem.id, this.storageLeg.id, {
      eventTime: value.eventTime || new Date(),
      facilityName: this.storageLeg.facilityName || value.locationText || undefined,
      notes: value.remarks || undefined,
      updatedBy: this.currentUserId(),
      handoff: this.buildHandoffContext(value)
    }), 'Storage received', () => this.receiveDialogVisible = false);
  }

  saveExit(): void {
    if (!this.caseItem?.id || !this.storageLeg?.id) {
      return;
    }
    const value = this.handoffForm.getRawValue();
    const overrideReason = (value.overrideReason || '').trim();
    if (!value.targetTransportLegId && !overrideReason) {
      this.messageService.add({ severity: 'warn', summary: 'Outbound transport required', detail: 'Select outbound transport or record an override reason.' });
      return;
    }
    this.submit(this.trackingService.exitStorageLeg(this.caseItem.id, this.storageLeg.id, {
      eventTime: value.eventTime || new Date(),
      notes: value.remarks || this.storageLeg.notes || undefined,
      updatedBy: this.currentUserId(),
      handoff: this.buildHandoffContext(value)
    }), 'Storage exited', () => this.exitDialogVisible = false);
  }

  saveSlot(): void {
    if (!this.caseItem?.id || !this.storageLeg?.id) {
      return;
    }
    const value = this.slotForm.getRawValue();
    const slot = this.yardSlots.find(item => item.id === value.yardSlotId);
    const slotCode = slot?.slotCode || value.slotCode;
    if (!slotCode) {
      this.messageService.add({ severity: 'warn', summary: 'Slot required', detail: 'Select a yard slot or enter a slot code.' });
      return;
    }
    this.submit(this.trackingService.assignStorageSlot(this.caseItem.id, this.storageLeg.id, {
      slotCode,
      notes: value.notes || undefined,
      updatedBy: this.currentUserId()
    }), 'Slot assigned', () => this.slotDialogVisible = false);
  }

  saveHold(): void {
    if (!this.caseItem?.id || !this.storageLeg?.id) {
      return;
    }
    const value = this.holdForm.getRawValue();
    this.submit(this.trackingService.holdStorageLeg(this.caseItem.id, this.storageLeg.id, {
      eventTime: value.eventTime || new Date(),
      notes: value.notes || undefined,
      updatedBy: this.currentUserId()
    }), 'Storage hold applied', () => this.holdDialogVisible = false);
  }

  saveRelease(): void {
    if (!this.caseItem?.id || !this.storageLeg?.id) {
      return;
    }
    const value = this.releaseForm.getRawValue();
    this.submit(this.trackingService.releaseStorageLeg(this.caseItem.id, this.storageLeg.id, {
      eventTime: value.eventTime || new Date(),
      notes: value.notes || undefined,
      updatedBy: this.currentUserId()
    }), 'Storage hold released', () => this.releaseDialogVisible = false);
  }

  saveYardMove(): void {
    if (!this.caseItem?.owningCompanyId || !this.yardVisit?.id || this.yardMoveForm.invalid) {
      this.yardMoveForm.markAllAsTouched();
      return;
    }
    const value = this.yardMoveForm.getRawValue();
    this.submit(this.terminalYardService.move(this.yardVisit.id, {
      owningCompanyId: this.caseItem.owningCompanyId,
      moveType: value.moveType,
      toAreaId: value.toAreaId || undefined,
      toSlotId: value.toSlotId || undefined,
      eventTime: value.eventTime || new Date(),
      referenceNumber: value.referenceNumber || undefined,
      remarks: value.remarks || undefined,
      createdBy: this.currentUserId()
    }), 'Yard move recorded', () => this.yardMoveDialogVisible = false);
  }

  saveGateOut(): void {
    if (!this.caseItem?.owningCompanyId || !this.yardVisit?.id || this.gateOutForm.invalid) {
      this.gateOutForm.markAllAsTouched();
      return;
    }
    const value = this.gateOutForm.getRawValue();
    this.submit(this.terminalYardService.gateOut(this.yardVisit.id, {
      owningCompanyId: this.caseItem.owningCompanyId,
      gateOutAt: value.gateOutAt || new Date(),
      emptyReturn: !!value.emptyReturn,
      outboundTransportLegId: value.outboundTransportLegId || undefined,
      referenceNumber: value.referenceNumber || undefined,
      sealVerified: value.sealVerified,
      sealMismatchReason: value.sealMismatchReason || undefined,
      conditionStatus: value.conditionStatus || undefined,
      damageObserved: value.damageObserved,
      damageNotes: value.damageNotes || undefined,
      remarks: value.remarks || undefined,
      createdBy: this.currentUserId()
    }), 'Terminal gate-out recorded', () => this.gateOutDialogVisible = false);
  }

  recalculateCharges(): void {
    if (this.isReferenceOnlyStorageLeg()) {
      return;
    }
    if (!this.caseItem?.owningCompanyId || !this.yardVisit?.id) {
      return;
    }
    this.submit(this.terminalYardService.recalculateCharges(this.caseItem.owningCompanyId, this.yardVisit.id),
      'Yard charges recalculated',
      () => undefined);
  }

  onSlotAreaChange(areaId?: number | null): void {
    const slotId = this.slotForm.get('yardSlotId')?.value;
    const slot = this.yardSlots.find(item => item.id === slotId);
    if (slot && areaId && slot.areaId !== areaId) {
      this.slotForm.patchValue({ yardSlotId: null, slotCode: '' }, { emitEvent: false });
    }
  }

  onSlotChange(slotId?: number | null): void {
    const slot = this.yardSlots.find(item => item.id === slotId);
    this.slotForm.patchValue({
      yardAreaId: slot?.areaId || this.slotForm.get('yardAreaId')?.value || null,
      slotCode: slot?.slotCode || this.slotForm.get('slotCode')?.value || ''
    }, { emitEvent: false });
  }

  areaOptions(): ContainerYardArea[] {
    const facilityId = this.storageLeg?.facilityId || this.yardVisit?.facilityId;
    return this.yardAreas.filter(area => area.active !== false && (!facilityId || area.facilityId === facilityId));
  }

  slotOptions(): ContainerYardSlot[] {
    const facilityId = this.storageLeg?.facilityId || this.yardVisit?.facilityId;
    const areaId = this.slotForm?.get('yardAreaId')?.value || this.yardMoveForm?.get('toAreaId')?.value;
    const currentSlotId = this.storageLeg?.yardSlotId || this.yardVisit?.currentSlotId;
    return this.yardSlots.filter(slot => {
      if (slot.active === false) return false;
      if (slot.occupied && slot.id !== currentSlotId) return false;
      if (facilityId && slot.facilityId !== facilityId) return false;
      return !areaId || slot.areaId === areaId;
    });
  }

  relatedHandoffs(): ContainerLegHandoff[] {
    const legId = this.storageLeg?.id;
    return this.handoffs.filter(handoff => handoff.fromStorageLegId === legId || handoff.toStorageLegId === legId);
  }

  storageTimeline(): ContainerEvent[] {
    const legId = this.storageLeg?.id;
    return this.timeline.filter(event => event.storageLegId === legId || event.sourceReference === this.storageLeg?.sourceReference);
  }

  statusSeverity(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'COMPLETED':
      case 'RELEASED': return 'success';
      case 'ACTIVE':
      case 'IN_PROGRESS':
      case 'GATED_IN':
      case 'IN_YARD': return 'warn';
      case 'PLANNED':
      case 'READY': return 'info';
      case 'EXCEPTION':
      case 'BLOCKED':
      case 'ON_HOLD': return 'danger';
      default: return 'secondary';
    }
  }

  storageFacilityLabel(type?: string): string {
    return this.formatCodeLabel(type) || 'Storage Facility';
  }

  facilityLabel(facilityId?: number | null, fallback?: string | null): string {
    if (!facilityId) return fallback || 'Facility pending';
    const facility = this.facilities.find(item => item.id === facilityId);
    return facility?.facilityName || fallback || 'Facility pending';
  }

  supplierOperatorLabel(): string {
    const operatorCrmClientId = this.storageLeg?.operatorCrmClientId
      || this.facilities.find(item => item.id === this.storageLeg?.facilityId)?.operatorCrmClientId;
    if (!operatorCrmClientId) {
      return 'Supplier not linked';
    }
    const supplier = this.supplierClients.find(item => item.id === operatorCrmClientId);
    return supplier?.companyName || supplier?.clientNumber || 'Supplier linked';
  }

  isReferenceOnlyStorageLeg(): boolean {
    const facility = this.facilities.find(item => item.id === this.storageLeg?.facilityId);
    return this.storageLeg?.storageControlMode === 'REFERENCE_ONLY' || facility?.operationMode === 'REFERENCE_ONLY';
  }

  yardAreaLabel(areaId?: number | null): string {
    if (!areaId) return 'Area pending';
    const area = this.yardAreas.find(item => item.id === areaId);
    return area?.areaName || area?.areaCode || 'Area pending';
  }

  yardSlotLabel(slotId?: number | null, fallback?: string | null): string {
    if (!slotId) return fallback || 'Slot pending';
    const slot = this.yardSlots.find(item => item.id === slotId);
    return slot?.slotCode || fallback || 'Slot pending';
  }

  transportLegLabel(legId?: number | null): string {
    if (!legId) return 'Transport not linked';
    const leg = this.transportLegs.find(item => item.id === legId);
    return leg ? `Leg ${leg.sequenceNo || leg.id} - ${this.formatCodeLabel(leg.mode) || 'Transport'}` : 'Transport linked';
  }

  handoffTypeLabel(type?: string): string {
    return this.formatCodeLabel(type) || 'Handoff';
  }

  dateTimeLabel(value?: Date | string | null): string {
    if (!value) return 'Pending';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'Pending' : parsed.toLocaleString();
  }

  moneyLabel(charge: ContainerYardChargeAccrual): string {
    const amount = Number(charge.chargeAmount || 0).toFixed(2);
    return `${charge.currencyCode || ''} ${amount}`.trim();
  }

  chargeTotalLabel(): string {
    if (!this.charges.length) {
      return 'No charges';
    }
    const currency = this.charges.find(charge => !!charge.currencyCode)?.currencyCode || 'SAR';
    const total = this.charges.reduce((sum, charge) => sum + Number(charge.chargeAmount || 0), 0);
    return `${currency} ${total.toFixed(2)}`;
  }

  applicableChargeRules(): ContainerYardChargeRule[] {
    const facilityId = this.storageLeg?.facilityId || this.yardVisit?.facilityId;
    const areaId = this.storageLeg?.yardAreaId || this.yardVisit?.currentAreaId;
    const containerType = (this.yardVisit?.isoEquipmentType || this.caseItem?.isoEquipmentType || '').toUpperCase();
    return this.chargeRules.filter(rule => {
      if (rule.active === false) return false;
      if (facilityId && rule.facilityId !== facilityId) return false;
      if (rule.areaId && areaId && rule.areaId !== areaId) return false;
      if (rule.containerType && containerType && !containerType.includes(rule.containerType.toUpperCase())) return false;
      return true;
    });
  }

  ruleRateLabel(rule: ContainerYardChargeRule): string {
    return `${rule.currencyCode || 'SAR'} ${Number(rule.rateAmount || 0).toFixed(2)} ${rule.chargeBasis === 'PER_DAY' ? '/ day' : 'flat'}`;
  }

  latestEir(): ContainerYardEir | undefined {
    return this.eirs.slice()
      .sort((a, b) => new Date(b.createdAt || b.eventTime || 0).getTime() - new Date(a.createdAt || a.eventTime || 0).getTime())[0];
  }

  openEir(eir?: ContainerYardEir): void {
    if (!eir?.id || !this.caseItem?.owningCompanyId) {
      return;
    }
    window.open(this.terminalYardService.eirPdfUrl(this.caseItem.owningCompanyId, eir.id), '_blank');
  }

  private initForms(): void {
    this.handoffForm = this.fb.group({
      eventTime: [new Date()],
      sourceTransportLegId: [null],
      targetTransportLegId: [null],
      sourceStorageLegId: [null],
      targetStorageLegId: [null],
      facilityType: [null],
      facilityId: [null],
      yardVisitId: [null],
      locationText: [''],
      overrideReason: [''],
      remarks: ['']
    });
    this.slotForm = this.fb.group({
      yardAreaId: [null],
      yardSlotId: [null],
      slotCode: [''],
      notes: ['']
    });
    this.holdForm = this.fb.group({
      eventTime: [new Date()],
      notes: ['']
    });
    this.releaseForm = this.fb.group({
      eventTime: [new Date()],
      notes: ['']
    });
    this.yardMoveForm = this.fb.group({
      moveType: ['YARD_MOVE', Validators.required],
      toAreaId: [null],
      toSlotId: [null],
      eventTime: [new Date(), Validators.required],
      referenceNumber: [''],
      remarks: ['']
    });
    this.gateOutForm = this.fb.group({
      gateOutAt: [new Date(), Validators.required],
      emptyReturn: [false],
      outboundTransportLegId: [null],
      referenceNumber: [''],
      sealVerified: [true],
      sealMismatchReason: [''],
      conditionStatus: ['UNKNOWN'],
      damageObserved: [false],
      damageNotes: [''],
      remarks: ['']
    });
  }

  private resetHandoffForm(action: 'receive' | 'exit'): void {
    this.handoffForm.reset({
      eventTime: new Date(),
      sourceTransportLegId: action === 'receive' ? this.storageLeg?.inboundTransportLegId || null : null,
      targetTransportLegId: action === 'exit' ? this.storageLeg?.outboundTransportLegId || null : null,
      sourceStorageLegId: this.storageLeg?.id || null,
      targetStorageLegId: this.storageLeg?.id || null,
      facilityType: this.storageLeg?.facilityType || null,
      facilityId: this.storageLeg?.facilityId || null,
      yardVisitId: this.storageLeg?.yardVisitId || this.yardVisit?.id || null,
      locationText: this.storageLeg?.facilityName || this.yardVisit?.currentLocationText || '',
      overrideReason: '',
      remarks: ''
    });
  }

  private buildHandoffContext(value: any): ContainerLegHandoffContext {
    return {
      sourceStorageLegId: value.sourceStorageLegId || undefined,
      targetStorageLegId: value.targetStorageLegId || undefined,
      sourceTransportLegId: value.sourceTransportLegId || undefined,
      targetTransportLegId: value.targetTransportLegId || undefined,
      facilityType: value.facilityType || undefined,
      facilityId: value.facilityId || undefined,
      yardVisitId: value.yardVisitId || undefined,
      locationText: value.locationText || undefined,
      overrideReason: value.overrideReason || undefined
    };
  }

  private loadYardContext(): void {
    if (!this.caseItem?.owningCompanyId || !this.caseItem.id) {
      return;
    }
    const companyId = this.caseItem.owningCompanyId;
    this.facilityService.findAll(new HttpParams()
      .set('owningCompanyId', String(companyId))
      .set('size', '200')
      .set('sort', 'facilityName,asc'))
      .subscribe({
        next: page => this.facilities = page.content || [],
        error: () => this.facilities = []
      });
    this.crmService.getClients(companyId, ['SUPPLIER', 'WAREHOUSE_SUPPLIER', 'TRANSPORTER']).subscribe({
      next: rows => this.supplierClients = rows || [],
      error: () => this.supplierClients = []
    });
    this.terminalYardService.findAreas(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: areas => this.yardAreas = areas || [],
      error: () => this.yardAreas = []
    });
    this.terminalYardService.findSlots(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: slots => this.yardSlots = slots || [],
      error: () => this.yardSlots = []
    });
    this.terminalYardService.findVisits(new HttpParams()
      .set('companyId', String(companyId))
      .set('containerCaseId', String(this.caseItem.id)))
      .subscribe({
        next: visits => {
          this.yardVisits = visits || [];
          this.yardVisit = this.yardVisits.find(visit => visit.storageLegId === this.storageLeg?.id)
            || this.yardVisits.find(visit => visit.id === this.storageLeg?.yardVisitId);
          this.loadYardVisitDetail();
        },
        error: () => this.yardVisits = []
      });
    this.terminalYardService.findAppointments(companyId).subscribe({
      next: appointments => this.appointments = (appointments || []).filter(item => item.visitId === this.storageLeg?.yardVisitId || item.containerCaseId === this.caseItem?.id),
      error: () => this.appointments = []
    });
    this.terminalYardService.findChargeRules(companyId).subscribe({
      next: rules => this.chargeRules = rules || [],
      error: () => this.chargeRules = []
    });
  }

  private loadYardVisitDetail(): void {
    if (!this.yardVisit?.id) {
      this.yardMoves = [];
      this.charges = [];
      this.eirs = [];
      return;
    }
    this.terminalYardService.findMoves(this.yardVisit.id).subscribe({
      next: moves => this.yardMoves = moves || [],
      error: () => this.yardMoves = []
    });
    this.terminalYardService.findCharges(this.yardVisit.id).subscribe({
      next: charges => this.charges = charges || [],
      error: () => this.charges = []
    });
    this.terminalYardService.findEirs(this.yardVisit.id).subscribe({
      next: eirs => this.eirs = eirs || [],
      error: () => this.eirs = []
    });
  }

  private submit(request: any, successDetail: string, closeDialog: () => void): void {
    this.isSubmitting = true;
    request.subscribe({
      next: () => {
        this.isSubmitting = false;
        closeDialog();
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: successDetail });
        if (this.caseItem?.id && this.storageLeg?.id) {
          this.load(this.caseItem.id, this.storageLeg.id);
        }
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Update failed', detail: error?.error?.message || 'Could not update storage control.' });
      }
    });
  }

  private currentUserId(): number | undefined {
    return this.sessionStorageService.getCustomer()?.id;
  }

  formatCodeLabel(value?: string | null): string {
    return (value || '')
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
